import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import Home from "@/pages/Home";
import Designs from "@/pages/Designs";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import CellularDAS from "@/pages/services/CellularDAS";
import UniFiInstallation from "@/pages/services/UniFiInstallation";
import PointToPointWireless from "@/pages/services/PointToPointWireless";
import BusinessWiFi from "@/pages/services/BusinessWiFi";
import CellBoosters from "@/pages/services/CellBoosters";
import SecurityCameras from "@/pages/services/SecurityCameras";
import AccessControl from "@/pages/services/AccessControl";
import CommercialNetworking from "@/pages/services/CommercialNetworking";
import Towers from "@/pages/services/Towers";
import WirelessInfrastructure from "@/pages/services/WirelessInfrastructure";
import SouthernAlberta from "@/pages/services/SouthernAlberta";
import Lethbridge from "@/pages/services/Lethbridge";

/**
 * True when the current hostname is the Designs subdomain.
 * Production:  designs.epicom.ca
 * Staging/preview: any host whose first label is "designs" (e.g. designs.localhost)
 * Override: set localStorage.forceDesignsHost = "true" for local testing.
 */
function isDesignsHost() {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage?.getItem("forceDesignsHost") === "true") return true;
  } catch (_err) {
    // localStorage can throw in Safari private mode / strict cookie settings — ignore.
  }
  const host = window.location.hostname.toLowerCase();
  return host === "designs.epicom.ca" || host.startsWith("designs.");
}

/** Detect Emergent Auth callback synchronously during render. */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Root route swaps between Home and Designs based on the hostname.
  // /designs continues to work as a direct path on both domains for sharing.
  const RootPage = isDesignsHost() ? Designs : Home;
  return (
    <Routes>
      <Route path="/" element={<RootPage />} />
      <Route path="/designs" element={<Designs />} />
      <Route path="/cellular-das" element={<CellularDAS />} />
      <Route path="/unifi-installation" element={<UniFiInstallation />} />
      <Route path="/point-to-point-wireless" element={<PointToPointWireless />} />
      <Route path="/business-wifi" element={<BusinessWiFi />} />
      <Route path="/cell-boosters" element={<CellBoosters />} />
      <Route path="/security-cameras" element={<SecurityCameras />} />
      <Route path="/access-control" element={<AccessControl />} />
      <Route path="/commercial-networking" element={<CommercialNetworking />} />
      <Route path="/towers" element={<Towers />} />
      <Route path="/wireless-infrastructure" element={<WirelessInfrastructure />} />
      <Route path="/southern-alberta" element={<SouthernAlberta />} />
      <Route path="/lethbridge" element={<Lethbridge />} />
      <Route path="/login" element={<Login />} />
      <Route path="/admin" element={<Admin />} />
    </Routes>
  );
}

/**
 * App tree WITHOUT a Router wrapper. Used by both the browser entry
 * (wrapped in BrowserRouter) and the Node prerender (wrapped in StaticRouter).
 * Keeping the Router external is the standard SSR pattern and is what makes
 * hydration deterministic — the rendered markup only depends on the URL the
 * router hands us, not on the browser it happens to be running in.
 */
export function AppShell() {
  return (
    <div className="App font-body bg-bg-primary text-white">
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#111315",
            color: "#fff",
            border: "1px solid #2A2E33",
            borderRadius: 0,
            fontFamily: "IBM Plex Sans, sans-serif",
          },
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
